import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MemoryMoreMenu from '@/components/memories/MemoryMoreMenu';
import StoryMediaThumb from '@/components/memories/StoryMediaThumb';
import type { StoryType } from '@/stores/models/Story';
import { formatMemoryAttachmentLabel, formatStoryDate } from '@/types/story';

import './memory-card.css';

interface MemoryCardProps {
  story: StoryType;
  familyId: string;
  showMemberChip?: boolean;
  memberName?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

function copyMemoryLink(familyId: string, storyId: string) {
  const url = `${window.location.origin}/families/${familyId}/memories/${storyId}`;
  void navigator.clipboard.writeText(url);
}

const MemoryCard: React.FC<MemoryCardProps> = ({
  story,
  familyId,
  showMemberChip = false,
  memberName,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('forms');

  const openDetail = () => {
    navigate(`/families/${familyId}/memories/${story.id}`);
  };

  const attachmentCount = Math.max(story.attachmentCount ?? 0, story.mediaAttachments.length);
  const hasAttachments = attachmentCount > 0;
  const attachmentLabel = formatMemoryAttachmentLabel(attachmentCount, story.mediaAttachments);

  return (
    <article
      className="memory-card"
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter') openDetail();
      }}
      role="link"
      tabIndex={0}
    >
      <div className="memory-card__head">
        {showMemberChip && memberName && <span className="memory-card__member-chip">{memberName}</span>}
        {story.isPending && <span className="memory-card__pending-badge">{t('story.pendingReview')}</span>}
        <div className="memory-card__menu" onClick={(e) => e.stopPropagation()}>
          <MemoryMoreMenu
            onOpen={openDetail}
            onCopyLink={() => copyMemoryLink(familyId, story.id)}
            onEdit={onEdit}
            onDelete={onDelete}
            showEdit={canEdit}
            showDelete={canDelete}
          />
        </div>
      </div>

      <h3 className="memory-card__title">{story.title}</h3>
      <p className="memory-card__excerpt">{story.excerpt}</p>

      <div className="memory-card__meta">
        <span>{story.authorLabel}</span>
        <span className="memory-card__sep">·</span>
        <span>{formatStoryDate(story.createdAt)}</span>
      </div>

      <div
        className={`memory-card__attachments${hasAttachments ? '' : ' memory-card__attachments--empty'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {hasAttachments ? (
          <>
            {story.mediaAttachments.slice(0, 4).map((attachment) => (
              <StoryMediaThumb
                key={attachment.id}
                familyId={familyId}
                mediaAssetId={attachment.mediaAssetId}
                mimeType={attachment.mimeType}
                size="40x40"
              />
            ))}
            <span className="memory-card__attachment-label">{attachmentLabel}</span>
          </>
        ) : (
          <span className="memory-card__attachments-empty">{t('story.noAttachments')}</span>
        )}
      </div>
    </article>
  );
};

export default MemoryCard;
